import { useMemo } from 'react';
import type { Industry } from '@/pages/Market/query/fetchIndustries';
import { getPath } from '@/router/utils';
import {
  ROUTE_MARKET,
  ROUTE_MARKET_SECTOR,
  ROUTE_MARKET_SECTOR_SECONDARY,
  ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
} from '@/constants/routes';
import type { PrimaryCountryISO } from '@/constants/countries';

import flatten from 'lodash/flatten';
import useIndustriesFetch from '@/pages/Market/hooks/useIndustriesFetch';

type IndustryOptions = {
  id: string;
  option: string;
  label: string;
  href: string;
  level: string;
}[];

const trendingIndustryIds: Array<string> = [
  '1030000',
  '2053000',
  '6110000',
  '8040000',
  '9551702',
];

export default function useIndustryOptions(
  includeTertiary = false,
  market?: PrimaryCountryISO,
  enabled?: boolean
): {
  options?: IndustryOptions;
  trendingIndustries: IndustryOptions;
  isFetched: boolean;
} {
  const { industries, isIndustriesFetched: isFetched } = useIndustriesFetch(
    market || 'us',
    true,
    enabled
  );

  const options = useMemo(
    () =>
      market
        ? [
            {
              label: 'All Industries',
              option: getPath({
                type: ROUTE_MARKET,
                payload: {
                  market,
                },
              }),
              href: getPath({
                type: ROUTE_MARKET,
                payload: {
                  market,
                },
              }),
              level: '1',
              id: '0',
            },
            ...Object.keys(industries.sector).reduce(
              (acc: IndustryOptions, industryId: string) => {
                const cur: Industry = industries.sector[industryId];
                const sectorPath = getPath({
                  type: ROUTE_MARKET_SECTOR,
                  payload: {
                    market,
                    sector: cur.slug,
                  },
                });
                const sectorOptions = {
                  id: industryId,
                  option: sectorPath,
                  label: cur.label,
                  level: '1',
                  href: sectorPath,
                };
                acc.push(sectorOptions);
                if (cur.children.length > 0 && Array.isArray(cur.children)) {
                  const secondaryChildren = flatten(
                    cur.children
                      .map(
                        (id) =>
                          industries.primary[id] &&
                          industries.primary[id].children
                      )
                      .filter(Boolean)
                  );
                  const secondaryOptions = secondaryChildren
                    .filter((s) => industries.secondary[s])
                    .reduce((accu: IndustryOptions, id: string) => {
                      const secondary = industries.secondary[id];
                      const secondaryPath = getPath({
                        type: ROUTE_MARKET_SECTOR_SECONDARY,
                        payload: {
                          market,
                          sector: cur.slug,
                          secondary: secondary.slug,
                        },
                      });
                      const secondaryOption = {
                        id,
                        option: secondaryPath,
                        label: secondary.label,
                        level: '2',
                        href: secondaryPath,
                      };
                      accu.push(secondaryOption);
                      if (secondary.children.length > 0 && includeTertiary) {
                        const tertiaryOptions = secondary.children
                          .map((i) => {
                            const tertiary = industries.tertiary[i];
                            if (tertiary) {
                              const tertiaryPath = getPath({
                                type: ROUTE_MARKET_SECTOR_SECONDARY_TERTIARY,
                                payload: {
                                  market,
                                  sector: cur.slug,
                                  secondary: secondary.slug,
                                  tertiary: tertiary.slug,
                                },
                              });
                              return {
                                id: i,
                                option: tertiaryPath,
                                label: tertiary.label,
                                level: '3',
                                href: tertiaryPath,
                              };
                            }
                          })
                          .filter((t) => t !== undefined);
                        accu.push(...(tertiaryOptions as IndustryOptions));
                      }
                      return accu;
                    }, []);
                  acc.push(...secondaryOptions);
                }
                return acc;
              },
              []
            ),
          ]
        : [],
    [includeTertiary, industries, market]
  );

  return {
    options,
    trendingIndustries: options.filter((i) =>
      trendingIndustryIds.includes(i.id)
    ),
    isFetched,
  };
}
